import type {
  ICreateDeferredRefetchSchedulerProps,
  IDeferredRefetchScheduler,
  IDeferredRefetchTimers,
  IScheduleDeferredRefetchProps,
  TDeferredRefetchQueryKey,
  TDeferredRefetchTimerHandle,
} from "./deferred-refetch-scheduler.types";

// NOTE: NSAI-5911 — push-driven refetches (a `notification:created`, a socket
// reconnect catch-up) used to call `invalidateQueries` on every event, which
// refetches at once whatever the app's visibility. Timer polling is skipped by
// React Query while the app is hidden; pushes were not, so a notification
// storm or a flapping connection kept a hidden tab fetching, writing the cache
// and re-rendering. This scheduler is the platform-agnostic half of the fix:
// every push marks its queries stale immediately (cheap, no request), and the
// actual refetch runs once per key — after a burst settles while visible, or
// on the return to visible while hidden. The platform supplies the visibility
// source and the query-client calls.

const defaultTimers: IDeferredRefetchTimers = {
  setTimeout: ({ callback, delayMs }) => setTimeout(callback, delayMs),
  clearTimeout: (handle) => {
    clearTimeout(handle);
  },
};

const hashQueryKey = (queryKey: TDeferredRefetchQueryKey): string =>
  JSON.stringify(queryKey);

export const createDeferredRefetchScheduler = ({
  isHidden,
  markStale,
  refetch,
  debounceMs,
  maxWaitMs,
  timers = defaultTimers,
}: ICreateDeferredRefetchSchedulerProps): IDeferredRefetchScheduler => {
  // NOTE: Insertion-ordered and keyed by hash, so a key pushed twenty times is
  // refetched once, in the order it was first scheduled.
  const pendingQueryKeys = new Map<string, TDeferredRefetchQueryKey>();
  let debounceHandle: TDeferredRefetchTimerHandle | null = null;
  let maxWaitHandle: TDeferredRefetchTimerHandle | null = null;

  const clearTimers = (): void => {
    if (debounceHandle !== null) {
      timers.clearTimeout(debounceHandle);
      debounceHandle = null;
    }
    if (maxWaitHandle !== null) {
      timers.clearTimeout(maxWaitHandle);
      maxWaitHandle = null;
    }
  };

  const flush = (): void => {
    clearTimers();
    if (pendingQueryKeys.size === 0) return;
    const queryKeys = [...pendingQueryKeys.values()];
    pendingQueryKeys.clear();
    for (const queryKey of queryKeys) {
      refetch(queryKey);
    }
  };

  // NOTE: A timer can outlive the visibility it was armed under (the tab is
  // hidden mid-burst), so the check is repeated here; while hidden the keys
  // stay pending for `handleVisible`.
  const handleTimerFired = (): void => {
    clearTimers();
    if (isHidden()) return;
    flush();
  };

  const schedule = ({ queryKeys }: IScheduleDeferredRefetchProps): void => {
    for (const queryKey of queryKeys) {
      markStale(queryKey);
      const hash = hashQueryKey(queryKey);
      if (!pendingQueryKeys.has(hash)) {
        pendingQueryKeys.set(hash, queryKey);
      }
    }
    if (pendingQueryKeys.size === 0) return;

    // NOTE: Nothing to arm while hidden: the return to visible is the trigger,
    // and a timer firing in a hidden tab would only re-check and go back to
    // waiting.
    if (isHidden()) {
      clearTimers();
      return;
    }

    if (debounceHandle !== null) {
      timers.clearTimeout(debounceHandle);
    }
    debounceHandle = timers.setTimeout({
      callback: handleTimerFired,
      delayMs: debounceMs,
    });
    if (maxWaitHandle === null) {
      maxWaitHandle = timers.setTimeout({
        callback: handleTimerFired,
        delayMs: maxWaitMs,
      });
    }
  };

  const handleVisible = (): void => {
    if (isHidden()) return;
    flush();
  };

  const reset = (): void => {
    clearTimers();
    pendingQueryKeys.clear();
  };

  const getPendingQueryKeys = (): TDeferredRefetchQueryKey[] => [
    ...pendingQueryKeys.values(),
  ];

  return { schedule, handleVisible, flush, reset, getPendingQueryKeys };
};
